import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import useTheme from '@mui/material/styles/useTheme';
import { DatePicker, type DateRange, DateRangePicker, DateTimePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { isAfter } from 'date-fns/isAfter';
import { isValid } from 'date-fns/isValid';
import { de } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import Iconify from '../../components/iconify/component';
import { type IconifyProps } from '../../components/iconify/types';
import { formatDate, formatTimestamp, isBefore, parseDate } from '../Date';
import { checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull } from '../Tools';
import InputNonEditValue from './helpers/render-non-edit-value';

export type InputDateTimeProps = {
    /** autoComplete - Name for AutoFill */
    autoComplete?: string;
    /** autoWidth - Disable FullWidth */
    autoWidth?: boolean;
    /** blink - Enable Blink-Animation */
    blink?: boolean;
    /** className - Class name */
    className?: string;
    /** customFormat - Custom format */
    customFormat?: string;
    /** disabled - Disable Input */
    disabled?: boolean;
    /** disableFuture - Disable Choosing Future */
    disableFuture?: boolean;
    /** disablePast - Disable Choosing Past */
    disablePast?: boolean;
    /** edit - Show Input-Elements, else show value only */
    edit?: boolean;
    /** endIcon - Show Icon at End */
    endIcon?: IconifyProps;
    /** error - Show Error-Message */
    error?: string;
    /** fatLabel - Toggle fat Label */
    fatLabel?: boolean;
    /** helperText - Additional Text for Help */
    helperText?: string;
    /** hideLabel - Hide Label in non-Edit-Mode */
    hideLabel?: boolean;
    /** icon - Icon for Label */
    icon?: IconifyProps;
    /** id - ID for Callback-Handler */
    id?: string;
    /** info - Info-Icon */
    info?: string;
    /** l - Label */
    l?: string;
    /** loading - Show Loading Animation */
    loading?: boolean;
    /** max - Maximum-Value */
    max?: number;
    /** min - Minimum-Value */
    min?: number;
    /** onKeyUp - Custom onKeyUp-Function */
    onKeyUp?: () => void;
    /** testid - ID for Testing */
    testid?: string;
    /** textAlign - Custom TextAlign */
    textAlign?: 'left' | 'center' | 'right' | 'justify';
} & (
    | {
          /** callback - Callback-Handler for Input */
          callback?: (e: { id: string; val: number }) => void;
          /** type - Choose Type of InputField (year, month, date, datetime, datetimeSec, time) */
          type?: 'year' | 'month' | 'date' | 'datetime' | 'datetimeSec' | 'time';
          /** v - Initial Value */
          v?: number;
          /** vNew - Value after Change */
          vNew?: number;
      }
    | {
          /** callback - Callback-Handler for Input */
          callback?: (e: { id: string; val: number }[]) => void;
          /** type - Choose Type of InputField (year, month, date, datetime, datetimeSec, time) */
          type?: 'range-year' | 'range-month' | 'range-date' | 'range-datetime' | 'range-datetimeSec' | 'range-time';
          /** v - Initial Value */
          v?: DateRange<number>;
          /** vNew - Value after Change */
          vNew?: DateRange<number>;
      }
);

let delaySubmitTimer: NodeJS.Timeout;

export function convertDateTimeValue(v: InputDateTimeProps['v'], type: InputDateTimeProps['type'], customFormat: InputDateTimeProps['customFormat']): string {
    if (!checkUndefinedOrNull(v)) {
        if (Array.isArray(v)) {
            return [
                !checkUndefinedOrNull(v[0]) && v[0] !== 0 && isValid(v[0])
                    ? formatDate(
                          v[0],
                          customFormat || type === 'range-year'
                              ? 'yyyy'
                              : type === 'range-month'
                                ? 'MM.yyyy'
                                : type === 'range-date'
                                  ? 'dd.MM.yyyy'
                                  : type === 'range-time'
                                    ? 'HH:mm'
                                    : type === 'range-datetimeSec'
                                      ? 'dd.MM.yyyy HH:mm:ss'
                                      : 'dd.MM.yyyy HH:mm',
                          undefined,
                          ['range-time', 'range-datetime', 'range-datetimeSec'].includes(type) ? ' Uhr' : undefined
                      )
                    : '-',
                !checkUndefinedOrNull(v[1]) && v[1] !== 0 && isValid(v[1])
                    ? formatDate(
                          v[1],
                          customFormat || type === 'range-year'
                              ? 'yyyy'
                              : type === 'range-month'
                                ? 'MM.yyyy'
                                : type === 'range-date'
                                  ? 'dd.MM.yyyy'
                                  : type === 'range-time'
                                    ? 'HH:mm'
                                    : type === 'range-datetimeSec'
                                      ? 'dd.MM.yyyy HH:mm:ss'
                                      : 'dd.MM.yyyy HH:mm',
                          undefined,
                          ['range-time', 'range-datetime', 'range-datetimeSec'].includes(type) ? ' Uhr' : undefined
                      )
                    : '-',
            ].join(' bis ');
        } else if (v !== 0 && isValid(v)) {
            return formatDate(
                v,
                customFormat ||
                    (type === 'year'
                        ? 'yyyy'
                        : type === 'month'
                          ? 'MM.yyyy'
                          : type === 'date'
                            ? 'dd.MM.yyyy'
                            : type === 'time'
                              ? 'HH:mm'
                              : type === 'datetimeSec'
                                ? 'dd.MM.yyyy HH:mm:ss'
                                : 'dd.MM.yyyy HH:mm'),
                undefined,
                ['time', 'datetime', 'datetimeSec'].includes(type) ? ' Uhr' : undefined
            );
        }
    } else {
        return '-';
    }
}

function InputDateTime(props: InputDateTimeProps) {
    const {
        autoWidth,
        autoComplete,
        blink,
        callback,
        customFormat,
        disabled,
        disableFuture,
        disablePast,
        edit,
        endIcon,
        error,
        fatLabel,
        helperText,
        hideLabel,
        icon,
        id,
        info,
        l,
        loading,
        max,
        min,
        onKeyUp,
        testid,
        textAlign,
        type = 'datetime',
        v,
        vNew,
    } = props;
    const [value, setValue] = useState<Date | DateRange<Date>>(null);
    const theme = useTheme();
    const minDate = !checkUndefinedOrNull(min) ? new Date(min) : disablePast ? new Date() : new Date('1900-01-01');
    const maxDate = !checkUndefinedOrNull(max) ? new Date(max) : disableFuture ? new Date() : new Date('2100-01-01');

    function handleSubmit(val: Date | DateRange<Date>) {
        if (!checkUndefinedOrNull(callback)) {
            if (Array.isArray(val)) {
                if ((checkUndefinedNullOrEmpty(val[0]) || !isBefore(val[0], minDate)) && (checkUndefinedNullOrEmpty(val[1]) || !isAfter(val[1], maxDate))) {
                    // @ts-expect-error
                    callback([
                        { id: id + 'Von', val: formatTimestamp(val[0]) || null },
                        { id: id + 'Bis', val: formatTimestamp(val[1]) || null },
                    ]);
                }
            } else {
                if (checkUndefinedOrNull(val) || (!isBefore(val, minDate) && !isAfter(val, maxDate))) {
                    // @ts-expect-error
                    callback({ id: id, val: formatTimestamp(val) || null });
                }
            }
        } else console.error('Callback-Funktion fehlt!\nInput-Key: inputDateTime_' + id);
    }

    function handleChange(val: Date | DateRange<Date>, delay?: boolean) {
        if (delay === true) {
            clearTimeout(delaySubmitTimer);
            delaySubmitTimer = setTimeout(() => {
                setValue(val || null);
                handleSubmit(val || null);
            }, 1000);
        } else {
            setValue(val || null);
            handleSubmit(val || null);
        }
    }

    const heading = [];
    if (!hideLabel) {
        if (icon) {
            heading.push(
                <span
                    key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'HeadingIcon'}
                    data-testid="icon"
                    style={{ color: disabled ? theme.palette.text.disabled : theme.palette.text.primary, lineHeight: 1, marginRight: 8 }}
                >
                    <Iconify icon={icon} />
                </span>
            );
        }
        if (l) heading.push(l);
    }

    useEffect(
        function () {
            if (type === 'range-date' || type === 'range-datetime' || type === 'range-datetimeSec' || type === 'range-month' || type === 'range-time' || type === 'range-year') {
                setValue([
                    !checkUndefined(vNew) && !checkUndefined(vNew[0])
                        ? !checkUndefinedOrNull(vNew[0])
                            ? vNew[0] instanceof Date
                                ? vNew[0]
                                : parseDate(vNew[0] as number, 'T')
                            : null
                        : !checkUndefinedOrNull(v[0])
                          ? v[0] instanceof Date
                              ? v[0]
                              : parseDate(v[0] as number, 'T')
                          : null,
                    !checkUndefined(vNew) && !checkUndefined(vNew[1])
                        ? !checkUndefinedOrNull(vNew[1])
                            ? vNew[1] instanceof Date
                                ? vNew[1]
                                : parseDate(vNew[1] as number, 'T')
                            : null
                        : !checkUndefinedOrNull(v[1])
                          ? v[1] instanceof Date
                              ? v[1]
                              : parseDate(v[1] as number, 'T')
                          : null,
                ]);
            } else {
                setValue(
                    !checkUndefined(vNew)
                        ? !checkUndefinedOrNull(vNew)
                            ? vNew instanceof Date
                                ? vNew
                                : parseDate(vNew as number, 'T')
                            : null
                        : !checkUndefinedOrNull(v)
                          ? v instanceof Date
                              ? v
                              : parseDate(v as number, 'T')
                          : null
                );
            }
        },
        [type, v, vNew]
    );

    useEffect(
        function () {
            if (blink) {
                setTimeout(() => {
                    const element = document.getElementById(id);
                    if (!checkUndefinedOrNull(element)) {
                        element.scrollIntoView({ block: 'center' });
                    }
                }, 500);
            }
        },
        [blink, id]
    );

    function textField(ownerState) {
        return {
            autoComplete: autoComplete ? 'section-cd ' + autoComplete : 'off',
            className: loading || icon ? 'outlinedStartAdornment' : undefined,
            error: !checkUndefinedNullOrEmpty(error),
            fullWidth: true,
            helperText: !checkUndefinedNullOrEmpty(error) ? error : !checkUndefinedNullOrEmpty(helperText) ? helperText : undefined,
            InputLabelProps: {
                shrink: !checkUndefined(ownerState.value),
            },
            InputProps: {
                onKeyUp: (e) => (!checkUndefinedOrNull(onKeyUp) && e.key === 'Enter' ? onKeyUp() : undefined),
            },
            inputProps: {
                'aria-label': l,
                'id': id,
                'style': { textAlign: textAlign },
            },
            label: l,
        };
    }

    return edit ? (
        <div
            data-testid={!checkUndefinedNullOrEmpty(testid) ? testid : id}
            className={[blink ? 'blinkInputfield' : '', 'inputDateTime'].join(' ')}
            key={'inputDateTime_' + id}
            style={{
                display: 'flex',
                justifyContent: textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : textAlign === 'justify' ? 'space-between' : 'flex-start',
                width: !autoWidth ? '100%' : undefined,
                marginBottom: !checkUndefinedOrNull(error) || !checkUndefinedOrNull(helperText) ? undefined : 10,
            }}
        >
            {(loading || !checkUndefinedOrNull(icon)) && (
                <div
                    key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'Icon'}
                    data-testid="icon"
                    style={{ display: 'flex', alignItems: 'center', color: !loading && !checkUndefinedOrNull(error) ? theme.palette.error.main : undefined, marginRight: 8, marginTop: 16 }}
                >
                    {loading ? <CircularProgress color="primary" size="1.5rem" /> : <Iconify icon={icon} />}
                </div>
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns} key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'DateProvider'} adapterLocale={de}>
                {type === 'range-date' || type === 'range-datetime' || type === 'range-datetimeSec' || type === 'range-month' || type === 'range-time' || type === 'range-year' ? (
                    <DateRangePicker
                        key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'Input'}
                        disabled={loading || disabled}
                        minDate={minDate}
                        maxDate={maxDate}
                        onChange={(val) => handleChange(val, true)}
                        slotProps={{
                            fieldRoot: {
                                sx: {
                                    flexGrow: 1,
                                },
                            },
                            textField,
                        }}
                        value={Array.isArray(value) ? value : [null, null]}
                    />
                ) : type === 'date' || type === 'month' || type === 'year' ? (
                    <DatePicker
                        key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'Input'}
                        disabled={loading || disabled}
                        minDate={minDate}
                        maxDate={maxDate}
                        onChange={(val) => handleChange(val, false)}
                        value={value}
                        slotProps={{ textField }}
                        format={type === 'year' ? 'yyyy' : type === 'month' ? 'MM.yyyy' : 'dd.MM.yyyy'}
                        openTo={type === 'date' ? 'day' : type}
                        views={type === 'year' ? ['year'] : type === 'month' ? ['year', 'month'] : ['year', 'month', 'day']}
                    />
                ) : type === 'datetime' || type === 'datetimeSec' ? (
                    <DateTimePicker
                        key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'Input'}
                        disabled={loading || disabled}
                        minDate={minDate}
                        maxDate={maxDate}
                        onChange={(val) => handleChange(val, false)}
                        value={value}
                        slotProps={{ textField }}
                        format={type === 'datetimeSec' ? 'dd.MM.yyyy HH:mm:ss' : 'dd.MM.yyyy HH:mm'}
                    />
                ) : (
                    <TimePicker
                        key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'Input'}
                        disabled={loading || disabled}
                        minTime={minDate}
                        maxTime={maxDate}
                        onChange={(val) => handleChange(val, false)}
                        value={value}
                        slotProps={{ textField }}
                        format="HH:mm"
                    />
                )}
            </LocalizationProvider>
            {!checkUndefinedOrNull(endIcon) && (
                <div
                    key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'EndIcon'}
                    data-testid="endIcon"
                    style={{ display: 'flex', alignItems: 'center', color: !loading && !checkUndefinedOrNull(error) ? theme.palette.error.main : undefined, marginLeft: 8, marginTop: 20 }}
                >
                    <Iconify icon={endIcon} />
                </div>
            )}
            {!checkUndefinedOrNull(info) && (
                <div
                    key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'InfoIcon'}
                    data-testid="infoIcon"
                    style={{ color: !loading && !checkUndefinedOrNull(error) ? theme.palette.error.main : undefined, marginLeft: 8, marginTop: 20 }}
                >
                    <Tooltip key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'InfoIconTooltip'} title={info}>
                        <span key={(!checkUndefinedNullOrEmpty(testid) ? testid : id) + 'InfoIconSpan'}>
                            <Iconify icon="mdi:information" color="primary.main" />
                        </span>
                    </Tooltip>
                </div>
            )}
        </div>
    ) : (
        <InputNonEditValue
            autoWidth={autoWidth}
            endIcon={endIcon}
            fatLabel={fatLabel}
            finalId={!checkUndefinedNullOrEmpty(testid) ? testid : id}
            hideLabel={hideLabel}
            icon={icon}
            info={info}
            l={l}
            loading={loading}
            textAlign={textAlign}
            value={convertDateTimeValue(v, type, customFormat)}
        />
    );
}

export default InputDateTime;
